import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

/* import project defined dependencies below and library/module dependency above */
import { DataTransferService } from '../../shared/services/data-transfer.service';
import { ErrorDialogService } from './error-dialog.service';
import { LocalStorageService } from './local-storage.service';

interface UploadLogoFiles {
    logo_images?: File;
    isDispatchEnabled?: string;
    customer_id?: string;
}

type UploadFile = File | { file: File };

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    public endpoint: any;
    public serviceUrl: any;
    public fileName: any;
    public file: any;
    public token: any;
    public wareHouseCode: any;
    public apiv1endpoint: any;

    constructor(
        private http: HttpClient,
        private dataTransferService: DataTransferService,
        private errorDialogService: ErrorDialogService,
        private localStorageService: LocalStorageService
    ) {
        this.endpoint = environment.APIENDPOINT;
        this.apiv1endpoint = environment.APIV1ENDPOINT;
    }

    private createHttpOptions(
        is_authorised?: boolean,
        withProgress?: boolean
    ) {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Language': 'en'
            })
        };

        // if (this.wareHouseCode) {
        //   httpOptions.headers = httpOptions.headers.set('warehouse', this.wareHouseCode);
        // }
        if (withProgress == true) {
            httpOptions['reportProgress'] = true;
        }

        return httpOptions;
    }

    private extractData<T>(res: unknown): T {
        if (
            typeof res !== 'object' ||
            res === null ||
            !('ok' in res) ||
            !('body' in res) ||
            !(res as { ok: boolean }).ok
        ) {
            return res as T;
        }

        const { body } = res as { body: ArrayBuffer };

        const decodedString = this.decodeBody(body);

        if (decodedString.includes('status_code')) {
            try {
                return JSON.parse(decodedString) as T;
            } catch {
                return {} as T;
            }
        }

        return {} as T;
    }

    private decodeBody(body: ArrayBuffer): string {
        const buffer = new Uint8Array(body);

        if (body.byteLength > 1000) {
            return btoa(
                Array.from(buffer).reduce((data, byte) => {
                    return data + String.fromCharCode(byte);
                }, '')
            );
        }

        return String.fromCharCode(...buffer);
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: unknown): Observable<T> => {
        //   console.error('handleError function:', error);
      
          if (
            typeof error === 'object' &&
            error !== null &&
            'error' in error &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            typeof (error as any).error === 'object' &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (error as any).error?.status_code === 1600
          ) {
            this.errorDialogService.openSubscriptionAlertDialog();
            // this.errorDialogService.openDialog(error);
          }
          return throwError(() => error);
        };
    }
    public callAuthPostApi(api_path, param, type?) {
        const http_options = this.createHttpOptions(true);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .post(this.serviceUrl, param, http_options)
            .pipe(
                map(this.extractData),
                catchError(this.handleError<any>('callAuthPostApi'))
            );
    }

    private getServiceUrl(api_path: string, type?: string): string {
        return type === 'jsonApi' ? this.apiv1endpoint + api_path : this.endpoint + api_path;
    }

    // commenting as not in use
    // public callNoAuthPatchApi(api_path, param, type?) {
    //     const http_options = this.createHttpOptions(true);
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .patch(this.serviceUrl, param, http_options)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callAuthPatchApi'))
    //         );
    // }

    public callAuthGetApi(api_path, type?, code?): Observable<any> {
        const http_options = code == 'noAuth' ? this.createHttpOptions(false) : this.createHttpOptions(true, false);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .get<any[]>(this.serviceUrl, http_options)
            .pipe(
                map(this.extractData),
                catchError(this.handleError<any>(code == 'noAuth' ? 'callNoAuthGetApi' : 'callAuthGetApi'))
            );
    }

    public getFileAsBlob(api_path, type?): Observable<Blob> {
        const httpOptions = {
            headers: new HttpHeaders({
                Accept: 'application/json',
                'X-Language': 'en'
            })
        };
        
        const responseTypeBlob = 'blob' as const;
        this.serviceUrl = this.getServiceUrl(api_path, type);

        return this.http
            .get(this.serviceUrl, {
                ...httpOptions,
                responseType: responseTypeBlob
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError<Blob>('callAuthGetApi'))
            );
    }

    // commenting as not in use
    // public getNoAuthFileAsBlob(api_path, type?): Observable<Blob> {
    //     const httpOptions = {
    //         headers: new HttpHeaders({
    //             Accept: 'application/json',
    //             'X-Language': 'en'
    //         })
    //     };
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .get(this.serviceUrl, {
    //             ...httpOptions,
    //             responseType: 'blob' as 'blob'
    //         })
    //         .pipe(
    //             map((res) => res),
    //             catchError(this.handleError<Blob>('callNoAuthGetApi'))
    //         );
    // }

    // commenting as not in use
    // public callNoAuthGetApi(api_path, type?) {
    //     const http_options = this.createHttpOptions(false);
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .get(this.serviceUrl, http_options)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callNoAuthGetApi'))
    //         );
    // }

    callUploadFilesPostServiceApi<T>(
        api_path: string,
        files: File | File[],
        filesType: string,
        type?: string
    ): Observable<T> {
        const formData = new FormData();
        const prams = {};
        const http_options = {
            headers: new HttpHeaders({
                Accept: '*/*',
                //'accept': 'application/vnd.api+json',
                'X-Language': 'en'
                // 'api-token': this.token
            }),
            reportProgress: true
            //withCredentials: true,
        };
        this.serviceUrl = this.getServiceUrl(api_path, type);
        if (Array.isArray(files)) {
            files.forEach((file) => {
                formData.append(filesType, file);
            });
        } else {
            formData.append(filesType, files);
        }
        return this.http
            .post(this.serviceUrl, formData, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(
                    this.handleError<T>('callUploadFilePostServiceApi')
                )
            );
    }

    // commenting as not in use
    // public noAuthGetApi(api_path, type?) {
    //     return this.http.get(api_path);
    // }

    public callNoAuthPostApi<T>(api_path, param, type?): Observable<T> {
        const http_options = this.createHttpOptions(false);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .post(this.serviceUrl, param, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(this.handleError<T>('callNoAuthPostApi'))
            );
    }

    public callPutApi<T>(api_path, param?, type?): Observable<T> {
        const http_options = this.createHttpOptions(true);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .put(this.serviceUrl, param, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(this.handleError<T>('callPostApi'))
            );
    }

    // commenting as not in use
    // callGetFileServiceApi(api_path, type?) {
    //     const httpOptions = {
    //         responseType: 'arraybuffer' as 'json',
    //         headers: new HttpHeaders({
    //             'Content-Type': 'application/json',
    //             Accept: 'application/json',
    //             'X-Language': 'en',
    //             'api-token': this.token
    //         }),
    //         observe: 'response' as 'body',
    //         withCredentials: true
    //     };

    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .get(this.serviceUrl, httpOptions)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callGetApi'))
    //         );
    // }

    callUploadFileServiceApi<T>(
        api_path: string,
        file: any,
        type?: string,
        shippingType?: any,
        data?: FormData
    ): Observable<T> {
        const actualFile = 'file' in file ? file.file : file;
        const fileName = actualFile.name;
        const formData = data ?? new FormData();
    
        const fileKey = api_path.includes('carrier_rules') ||
                        api_path.includes('upload_warehouses_for_zipcode') ||
                        api_path.includes('upload_warehouses_for_city') ||
                        api_path.includes('unmapping_product_by_sku')
            ? 'file'
            : 'excelfile';
        
        formData.append(fileKey, actualFile, fileName);
    
        if (shippingType > 0) {
            const typeKey = api_path.includes('shipment') ? 'prod_type' : 'shipment_type_id';
            formData.append(typeKey, shippingType);
        }

        if (['49F', '79F', 'A-Shipping', 'asendia_ui'].includes(shippingType)) {
            formData.append('template_name', shippingType);
        }
        let http_options: any = {
            headers: new HttpHeaders({
                Accept: '*/*',
                'X-Language': 'en'
            }),
            reportProgress: true
        };
    
        if (type === 'blob') {
            http_options = { ...http_options, responseType: 'blob' };
        }
    
        this.serviceUrl = this.getServiceUrl(api_path, type);
    
        return this.http
            .post(this.serviceUrl, formData, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(this.handleError<T>('callUploadFileServiceApi'))
            );
    }
    
    // commenting as not in use
    // callUploadFilePatchServiceApi(
    //     api_path: string,
    //     file: any,
    //     type?: string
    // ) {
    //     const formData = new FormData();
    
    //     this.appendFileToFormData(file, formData);
    
    //     const http_options = this.prepareHttpOptions();
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    
    //     return this.http.patch(this.serviceUrl, formData, http_options).pipe(
    //         map(this.extractData),
    //         catchError(this.handleError<any>('callUploadFilePatchServiceApi'))
    //     );
    // }

    // private appendFileToFormData(file: any, formData: FormData): void {
    //     if (file.onboarding_file) {
    //         formData.append('onboarding_file[attachment]', file.onboarding_file);
    //     } else if (file.bulk_activity) {
    //         formData.append('bulk_activity[attachment]', file.bulk_activity);
    //     } else if (file.users) {
    //         this.appendNestedFields(formData, 'user', file.users);
    //     } else if (file.company_contract) {
    //         this.appendNestedFields(formData, 'company_contract', file.company_contract);
    //     } else {
    //         this.file = file.file || file;
    //         this.fileName = this.file.name;
    //         formData.append('file', this.file, this.fileName);
    
    //         if (file.type) formData.append('type', file.type);
    //         if (file.name) formData.append('name', file.name);
    //     }
    // }
    
    // private appendNestedFields(formData: FormData, keyPrefix: string, items: any[]): void {
    //     items.forEach(item => {
    //         Object.keys(item).forEach(key => {
    //             formData.append(`${keyPrefix}[${key}]`, item[key]);
    //         });
    //     });
    // }
    
    // private prepareHttpOptions(): { headers: HttpHeaders; reportProgress: boolean } {
    //     let headers = new HttpHeaders({
    //         Accept: '*/*',
    //         'X-Language': 'en',
    //         Authorization: 'Bearer ' + this.token
    //     });
    
    //     const wareHouseCode = this.localStorageService.get('wareHouseCode');
    //     if (wareHouseCode) {
    //         headers = headers.set('warehouse', wareHouseCode);
    //     }
    
    //     return {
    //         headers,
    //         reportProgress: true
    //     };
    // }

    // commenting as not in use
    // public callDeleteApi(api_path: any, type?: string) {
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .delete(this.serviceUrl)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callDeleteApi'))
    //         );
    // }

    public callAuthDeleteApi<T>(api_path, param, type?): Observable<T> {
        const http_options = this.createHttpOptions(true);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        http_options['body'] = param;
        return this.http
            .delete(this.serviceUrl, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(this.handleError<T>('callPostApi'))
            );
    }

    // commenting as not in use
    // callGetFilePostServiceApi(api_path, param, type?) {
    //     const httpOptions = {
    //         responseType: 'arraybuffer' as 'json',
    //         headers: new HttpHeaders({
    //             'Content-Type': 'application/json',
    //             Accept: 'application/json',
    //             'X-Language': 'en',
    //             Authorization: 'Bearer ' + this.token
    //         }),
    //         observe: 'response' as 'body'
    //     };

    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     return this.http
    //         .post(this.serviceUrl, param, httpOptions)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callGetApi'))
    //         );
    // }

    // commenting as not in use
    // callUploadImageFileServiceApi(
    //     api_path: string,
    //     file: any,
    //     type?: string
    // ) {
    //     const formData = new FormData();
    //     if (file.length > 0) {
    //         file.forEach((value, key) => {
    //             if (value) {
    //                 formData.append('product[product_images][]', value);
    //             }
    //         });
    //     }
    //     this.wareHouseCode = this.localStorageService.get('wareHouseCode');
    //     const http_options = {
    //         headers: new HttpHeaders({
    //             Accept: '*/*',
    //             'X-Language': 'en',
    //             Authorization: 'Bearer ' + this.token
    //         }),
    //         reportProgress: true
    //     };
    //     if (this.wareHouseCode) {
    //         http_options.headers = http_options.headers.set(
    //             'warehouse',
    //             this.wareHouseCode
    //         );
    //     }
    //     this.serviceUrl = this.getServiceUrl(api_path, type);

    //     return this.http
    //         .put(this.serviceUrl, formData, http_options)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(
    //                 this.handleError<any>('callUploadFilePatchServiceApi')
    //             )
    //         );
    // }

    // Put API

    // commenting as not in use
    // callUploadFilePutServiceApi(
    //     api_path: string,
    //     file: any,
    //     type?: string
    // ) {
    //     this.token = this.localStorageService.get('userToken');
    //     const formData = new FormData();
    //     const prams = {};
    //     const http_options = {
    //         headers: new HttpHeaders({
    //             Accept: '*/*',
    //             //'accept': 'application/vnd.api+json',
    //             'X-Language': 'en',
    //             Authorization: 'Bearer ' + this.token
    //             // 'api-token': this.token
    //         }),
    //         reportProgress: true
    //         //withCredentials: true,
    //     };
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     formData.append('excelfile', file);
    //     return this.http
    //         .put(this.serviceUrl, formData, http_options)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callUploadFilePutServiceApi'))
    //         );
    // }

    // commenting as not in use
    // callUploadFilesPutServiceApi(
    //     api_path: string,
    //     files: any,
    //     filesType: string,
    //     type?: string
    // ) {
    //     this.token = this.localStorageService.get('userToken');
    //     const formData = new FormData();
    //     const prams = {};
    //     const http_options = {
    //         headers: new HttpHeaders({
    //             Accept: '*/*',
    //             //'accept': 'application/vnd.api+json',
    //             'X-Language': 'en',
    //             Authorization: 'Bearer ' + this.token
    //             // 'api-token': this.token
    //         }),
    //         reportProgress: true
    //         //withCredentials: true,
    //     };
    //     this.serviceUrl = this.getServiceUrl(api_path, type);
    //     files.forEach((element) => {
    //         formData.append(filesType, element);
    //     });
    //     return this.http
    //         .put(this.serviceUrl, formData, http_options)
    //         .pipe(
    //             map(this.extractData),
    //             catchError(this.handleError<any>('callUploadFilePutServiceApi'))
    //         );
    // }

    public callAuthGetMasterApi<T>(api_path: string, type?: string): Observable<T> {
        const http_options = this.createHttpOptions(true, false);
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .get(this.serviceUrl, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(this.handleError<T>('callAuthGetMasterApi'))
            );
    }

    callUploadFilesPostServiceApiLogo<T>(
        api_path: string,
        files: UploadLogoFiles,
        type?: string
    ): Observable<T> {
        const formData = new FormData();
        const http_options = {
            headers: new HttpHeaders({
                Accept: '*/*',
                'X-Language': 'en'
            }),
            reportProgress: true
        };
        this.serviceUrl = this.getServiceUrl(api_path, type);

        if (files.isDispatchEnabled) {
            formData.append('is_dispatch_enabled', files.isDispatchEnabled);
        }
        if (files.logo_images) {
            formData.append('logo_image', files.logo_images);
        }
        if (files.customer_id) {
            formData.append('customer_id', files.customer_id);
        }
        return this.http
            .post(this.serviceUrl, formData, http_options)
            .pipe(
                map((res) => this.extractData<T>(res)),
                catchError(
                    this.handleError<T>('callUploadFilePostServiceApi')
                )
            );
    }

    public postFileAsBlob(api_path, data, type?) {
        const httpOptions = {
            headers: new HttpHeaders({
                Accept: 'application/json',
                'X-Language': 'en'
            })
        };
        const responseTypeBlob = 'blob' as const;
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .post(this.serviceUrl, data, {
                ...httpOptions,
                responseType: responseTypeBlob
            })
            .pipe(
                map((res) => res),
                catchError(this.handleError<Blob>('callAuthGetApi'))
            );
    }

    /** As postFileAsBlob, with the whole response so its headers can be read. */
    public postFileAsBlobResponse(
        api_path,
        data,
        type?
    ): Observable<HttpResponse<Blob>> {
        const httpOptions = {
            headers: new HttpHeaders({
                Accept: 'application/json',
                'X-Language': 'en'
            })
        };
        this.serviceUrl = this.getServiceUrl(api_path, type);
        return this.http
            .post(this.serviceUrl, data, {
                ...httpOptions,
                responseType: 'blob' as const,
                observe: 'response' as const
            })
            .pipe(
                catchError(
                    this.handleError<HttpResponse<Blob>>('postFileAsBlobResponse')
                )
            );
    }
}
